import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Illustration as Owlbert } from "@/components/Illustration";
import { subjectStyleFor, STICKER_TILT } from "@/lib/subjectStyles";
import { Layers as IcoLayers, ListChecks as IcoList, Sparkles as IcoSpark, BookOpen as IcoBook, ChevronRight as IcoChevron, Target as IcoTarget, ArrowLeft as IcoBack, History as IcoHistory, Zap } from "lucide-react";
import { 
  BarChart3, 
  Plus, 
  Shuffle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Clock,
  Play,
  Sparkles,
  Edit,
  Book,
  PenTool,
  BookOpen,
  Lightbulb,
  Target,
  FlaskConical,
  Flag
} from "lucide-react";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getExtraHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { QuestionRenderer, OptionRenderer, ExplanationRenderer } from "@/components/QuestionRenderer";
import { MathRenderer } from "@/components/MathRenderer";
import { LatexText } from "@/components/LatexText";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import DOMPurify from 'dompurify';
import { trackEvent } from "@/lib/mixpanel";
import { capturePostHogEvent } from "@/lib/posthog";
import { getTopicMetadata } from "@/lib/topicMetadata";
import { LessonCard } from "@/pages/LessonLibrary";
import { gradeAnswer } from "@shared/grading";

interface KidExplanation {
  bigIdea: string; whatWentWrong: string; steps: string[]; answer: string;
  tryThis: { question: string; answer: string }; plain: string;
}

/** "Explain it to me": the slip, the working, the answer, then a quick try-it, in a child's terms. */
function KidExplanationPanel({ kid }: { kid: KidExplanation }) {
  const [showTry, setShowTry] = useState(false);
  return (
    <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 px-4 py-3 text-sm text-foreground space-y-3" data-testid="kid-explanation">
      {kid.bigIdea && <p className="font-semibold">{kid.bigIdea}</p>}
      {kid.whatWentWrong && (
        <p><span className="font-semibold text-amber-700 dark:text-amber-400">What went wrong: </span>{kid.whatWentWrong}</p>
      )}
      {kid.steps?.length > 0 && (
        <ol className="space-y-1.5 list-none pl-0">
          {kid.steps.map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="shrink-0 w-6 h-6 rounded-md bg-violet-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
      )}
      {kid.answer && <p><span className="font-semibold text-emerald-700 dark:text-emerald-400">Answer: </span>{kid.answer}</p>}
      {kid.tryThis?.question && (
        <div className="rounded-md bg-white/70 dark:bg-black/20 px-3 py-2">
          <p><span className="font-semibold">Try this: </span>{kid.tryThis.question}</p>
          {showTry ? (
            <p className="mt-1 text-emerald-700 dark:text-emerald-400 font-medium">Answer: {kid.tryThis.answer}</p>
          ) : (
            <button type="button" className="mt-1 text-xs font-semibold text-violet-700 dark:text-violet-300 underline" onClick={() => setShowTry(true)}>Show the answer</button>
          )}
        </div>
      )}
    </div>
  );
}

interface Question {
  id: string;
  questionType: "multiple-choice" | "multi-select" | "true-false" | "fill-blank" | "order-match" | "table-grid" | "matching";
  topic: string;
  difficulty: "easy" | "medium" | "hard";
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  options?: Array<string | { id?: string; text: string; imageUrl?: string; math?: string }>;
  correctAnswer: string;
  explanation: string;
  explanationImageUrl?: string;
  explanationMath?: string;
  aiExplanation?: string;
  points: number;
  groupId?: string;
  groupOrder?: number;
  groupStimulus?: string;
}

interface Topic {
  id: string;
  name: string;
  icon: any;
  color: string;
  description: string;
  questionsCount: number;
  completed: number;
  progress: number;
}

function shuffleWithGroups(questions: Question[]): Question[] {
  const groups = new Map<string, Question[]>();
  const standalones: Question[][] = [];
  
  questions.forEach(q => {
    if (q.groupId) {
      if (!groups.has(q.groupId)) groups.set(q.groupId, []);
      groups.get(q.groupId)!.push(q);
    } else {
      standalones.push([q]);
    }
  });
  
  groups.forEach(group => group.sort((a, b) => (a.groupOrder || 0) - (b.groupOrder || 0)));
  
  const allBlocks = [...groups.values(), ...standalones];
  for (let i = allBlocks.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allBlocks[i], allBlocks[j]] = [allBlocks[j], allBlocks[i]];
  }
  
  return allBlocks.flat();
}

const EMPTY_QUESTIONS: Question[] = [];
const EMPTY_READING: any[] = [];
const EMPTY_LESSONS: any[] = [];

/** "Language Arts 2" / "Mathematics (Internal)" -> "language arts" / "mathematics" */
function subjectKey(name: string): string {
  return name.toLowerCase().replace(/\s*\((internal|g\d+)\)\s*$/, "").replace(/\s+\d+$/, "").trim();
}

const Learning = () => {
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopicState] = useState<string | null>(null);
  const [selectedMode, setSelectedModeState] = useState<string | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<string | null>(null);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[] | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  // Per-question outcomes for the results page review list (saved to localStorage with the results).
  const reviewRef = useRef<Array<{ id: string; questionText: string; chosen: string; correct: string; isCorrect: boolean; explanation?: string }>>([]);
  const [correctQuestions, setCorrectQuestions] = useState<Set<string>>(new Set());
  const [testStartTime, setTestStartTime] = useState<Date>(new Date());
  const [showAIExplanation, setShowAIExplanation] = useState(false);
  const [aiExplanationText, setAIExplanationText] = useState<string | null>(null);
  const [kidExplanation, setKidExplanation] = useState<KidExplanation | null>(null);
  // Tracks the result of the last graded submission so the UI feedback card matches what was
  // sent to the server. Computed string-equality on selectedAnswer === correctAnswer breaks for
  // compound types (multi-select arrays, matching objects, etc.): use this instead.
  const [lastSubmissionCorrect, setLastSubmissionCorrect] = useState<boolean | null>(null);
  
  // Per-question time tracking
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  
  // Order-match question state
  const [orderedItems, setOrderedItems] = useState<any[]>([]);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [tapSelectedItem, setTapSelectedItem] = useState<number | null>(null);
  const touchDragRef = useRef<{ startY: number; startX: number; index: number; moved: boolean; holdTimer?: ReturnType<typeof setTimeout>; itemHeight: number } | null>(null);
  const orderListRef = useRef<HTMLDivElement>(null);
  const [touchDraggingIdx, setTouchDraggingIdx] = useState<number | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{ x: number; y: number; text: string } | null>(null);
  const scrollLockRef = useRef(false);

  // Global scroll lock: iOS Safari ignores body overflow:hidden for touch scroll
  useEffect(() => {
    const prevent = (e: TouchEvent) => {
      if (scrollLockRef.current) e.preventDefault();
    };
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => document.removeEventListener('touchmove', prevent);
  }, []);

  // Table-grid question state
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  // Matching question state
  const [matchingSelections, setMatchingSelections] = useState<Record<string, string>>({});
  const [activePrompt, setActivePrompt] = useState<string | null>(null);
  const [draggingAnswer, setDraggingAnswer] = useState<string | null>(null);

  // Report question state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  
  const { user, isAuthenticated } = useAuth();
  // Review mode: lets grade 5/6 students revise the grade below. Practice here is flagged review
  // server-side and earns no XP, points, badges, streaks or prize progress.
  const [reviewGrade, setReviewGrade] = useState<number | null>(null);
  const canReview = !!user?.grade && user.grade > 4;
  const effectiveGrade = reviewGrade ?? user?.grade;

  // Cross-grade review link for the current topic (shown only in normal mode, not review).
  const { data: prereqData } = useQuery<{ prereq: { subject: string; grade: number; topicKey: string; topicDisplay: string } | null }>({
    queryKey: ['/api/review/prereq', selectedSubject, selectedTopic],
    queryFn: async () => {
      const r = await fetch(`/api/review/prereq?subject=${encodeURIComponent(selectedSubject || '')}&topic=${encodeURIComponent(selectedTopic || '')}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!r.ok) return { prereq: null };
      return r.json();
    },
    enabled: isAuthenticated && !!selectedSubject && !!selectedTopic && !reviewGrade,
  });
  const topicPrereq = prereqData?.prereq ?? null;

  const trackReviewEntered = (source: string, toGrade: number | null) => {
    try { trackEvent('Review Entered', { source, fromGrade: user?.grade ?? null, toGrade }); } catch {}
    fetch('/api/review/track', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getExtraHeaders() }, body: JSON.stringify({ source, toGrade }) }).catch(() => {});
  };
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subjects from API - grade-specific to avoid topic mismatch
  const { data: subjects = [] } = useQuery<Array<{id: string; name: string; displayName: string; description?: string | null; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/subjects', effectiveGrade],
    enabled: isAuthenticated && !!user && !!effectiveGrade,
  });

  const activeSubjects = subjects
    .filter(s => s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Keyboard focus among quiz options (arrow keys); -1 = none.
  const [focusedOption, setFocusedOption] = useState(-1);

  // What each subject holds and where the student left off (Subjects page cards).
  type SubjectOverview = { id: string; name: string; description: string | null; topics: number; questions: number; lessons: number; readers: number; answered: number; correct: number; accuracy: number | null; completedPct: number; topicsStarted: number; lastTopic: { id: string; name: string; at: string } | null };
  const { data: overview } = useQuery<{ grade: number; subjects: SubjectOverview[] }>({
    queryKey: ['/api/learning/overview', effectiveGrade],
    queryFn: async () => {
      const r = await fetch(`/api/learning/overview?grade=${effectiveGrade ?? 6}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!r.ok) throw new Error('overview');
      return r.json();
    },
    enabled: isAuthenticated && !!user && !!effectiveGrade && !selectedSubject,
    staleTime: 30_000,
  });

  // Check for subject parameter in URL and auto-select subject on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subjectParam = params.get('subject');
    if (subjectParam && !selectedSubject) {
      setSelectedSubject(subjectParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mutations for progress tracking
  const recordAttemptMutation = useMutation({
    mutationFn: async (attemptData: {
      questionId: string;
      subject: string;
      topic: string;
      difficulty: string;
      selectedAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
      basePoints: number;
      pointsEarned: number;
      timeSpent?: number;
    }) => {
      const response = await apiRequest('POST', '/api/attempts', attemptData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/report-card'] });
      queryClient.invalidateQueries({ queryKey: ['/api/report-card/objectives'] });
    },
    onError: (error) => {
      console.error('Failed to record attempt:', error);
      toast({
        title: "Connection Error",
        description: "Failed to save your progress. Please check your connection.",
        variant: "destructive",
      });
    }
  });

  const updateProgressMutation = useMutation({
    mutationFn: async (progressData: {
      subject: string;
      topic: string;
      totalQuestions: number;
      correctAnswers: number;
      totalPoints: number;
      completedAt?: string;
    }) => {
      const response = await apiRequest('POST', '/api/progress', progressData);
      return response.json();
    },
    onSuccess: () => {
      // Invalidate progress queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['/api/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
    },
    onError: (error) => {
      console.error('Failed to update progress:', error);
    }
  });

  // Test results mutation for anti-grinding system
  const testResultsMutation = useMutation({
    mutationFn: async (testData: {
      subject: string;
      topic: string;
      totalQuestions: number;
      correctAnswers: number;
      totalTimeSpent: number;
      difficulty: string;
    }) => {
      const response = await apiRequest('POST', '/api/test/results', testData);
      return response.json();
    },
    onSuccess: (resultsData) => {
      // Store results in localStorage for the results page
      localStorage.setItem('lastTestResults', JSON.stringify(resultsData));
      try { localStorage.setItem('lastTestReview', JSON.stringify(reviewRef.current)); } catch {}
      try { localStorage.setItem('lastQuizPath', window.location.pathname + window.location.search); } catch {}
      
      // Track quiz completion event
      trackEvent('Quiz Completed', {
        subject: resultsData.testSummary.subject,
        topic: resultsData.testSummary.topic,
        totalQuestions: resultsData.testSummary.totalQuestions,
        correctAnswers: resultsData.testSummary.correctAnswers,
        accuracy: resultsData.testSummary.accuracy,
        difficulty: resultsData.testSummary.difficulty,
        xpGained: resultsData.levelProgress.xpGained,
        timeSpent: resultsData.testSummary.totalTimeSpent,
      });
      capturePostHogEvent('Quiz Completed', {
        subject: resultsData.testSummary.subject,
        topic: resultsData.testSummary.topic,
        total_questions: resultsData.testSummary.totalQuestions,
        correct_answers: resultsData.testSummary.correctAnswers,
        accuracy: resultsData.testSummary.accuracy,
        difficulty: resultsData.testSummary.difficulty,
        xp_gained: resultsData.levelProgress.xpGained,
        time_spent: resultsData.testSummary.totalTimeSpent,
      });
      
      queryClient.invalidateQueries({ queryKey: ['/api/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/level'] });
      queryClient.invalidateQueries({ queryKey: ['/api/report-card'] });
      queryClient.invalidateQueries({ queryKey: ['/api/report-card/objectives'] });
      
      // Redirect to results page
      setLocation('/results');
    },
    onError: (error) => {
      console.error('Failed to process test results:', error);
      toast({
        title: "Couldn't save your results",
        description: "Something went wrong saving this session. Please check your connection and try submitting again.",
        variant: "destructive",
      });
    }
  });

  // AI Explanation generation mutation
  const generateExplanationMutation = useMutation({
    mutationFn: async ({ questionId, chosenAnswer }: { questionId: string; chosenAnswer?: string }) => {
      const response = await apiRequest('POST', `/api/questions/${questionId}/generate-explanation`, { chosenAnswer });
      return response.json();
    },
    onSuccess: (data, variables) => {
      // The student may already be on the next question by the time this lands.
      if (variables.questionId !== currentQuestionIdRef.current) return;
      setAIExplanationText(data.explanation);
      setKidExplanation(data.kid ?? null);
      setShowAIExplanation(true);
    },
    onError: (error) => {
      console.error('Failed to generate explanation:', error);
    }
  });
  const currentQuestionIdRef = useRef<string | undefined>(undefined);

  const [reportedQuestionIds, setReportedQuestionIds] = useState<Set<string>>(new Set());

  const reportQuestionMutation = useMutation({
    mutationFn: async ({ questionId, reason, details }: { questionId: string; reason: string; details?: string }) => {
      const response = await apiRequest('POST', `/api/questions/${questionId}/report`, { reason, details });
      return response.json();
    },
    onSuccess: (_data, variables) => {
      setReportDialogOpen(false);
      setReportReason("");
      setReportDetails("");
      // Strike the question from this session: it no longer counts for or against the student,
      // and dropping it from the list moves them straight on to the next question.
      const reported = shuffledQuestions.find(q => q.id === variables.questionId);
      if (reported && correctQuestions.has(reported.id)) setScore(s => Math.max(0, s - (reported.points || 0)));
      setCorrectQuestions(prev => { const next = new Set(prev); next.delete(variables.questionId); return next; });
      setAnsweredQuestions(prev => { const next = new Set(prev); next.delete(variables.questionId); return next; });
      setReportedQuestionIds(prev => new Set([...prev, variables.questionId]));
      setSelectedAnswer(null);
      setShowFeedback(false);
      setShowAIExplanation(false);
      setAIExplanationText(null);
      setKidExplanation(null);
      toast({
        title: "Thanks, we'll check it",
        description: "That question is skipped and won't count towards your score.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Report Failed",
        description: err?.message || "Could not submit your report. Please try again.",
        variant: "destructive",
      });
    }
  });

  const { 
    data: fetchedQuestions = EMPTY_QUESTIONS, 
    isLoading: questionsLoading, 
    error: questionsError 
  } = useQuery({
    queryKey: ['/api/questions/adaptive', selectedSubject, selectedTopic, selectedMode, selectedObjective, reviewGrade],
    queryFn: async () => {
      if (selectedObjective) {
        const response = await fetch(`/api/questions/by-objective/${encodeURIComponent(selectedObjective)}${reviewGrade ? `?reviewGrade=${reviewGrade}` : ''}`, { credentials: 'include', headers: getExtraHeaders() });
        if (!response.ok) throw new Error('Failed to fetch questions');
        return response.json();
      }
      if (!selectedTopic || !selectedSubject) return [];
      const response = await fetch(`/api/questions/adaptive/${encodeURIComponent(selectedTopic)}?subject=${selectedSubject}${reviewGrade ? `&reviewGrade=${reviewGrade}` : ''}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!response.ok) throw new Error('Failed to fetch questions');
      return response.json();
    },
    enabled: (!!selectedObjective || (!!selectedTopic && !!selectedSubject)) && !!isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!search) {
      setSelectedSubject(null);
      setSelectedTopicState(null);
      setSelectedModeState(null);
      setSelectedObjective(null);
      setReviewGrade(null);
      return;
    }
    const params = new URLSearchParams(search);
    const subject = params.get('subject');
    const topic = params.get('topic');
    const mode = params.get('mode');
    const objective = params.get('objective');
    if (subject) setSelectedSubject(subject);
    setSelectedTopicState(topic);
    setSelectedModeState(mode);
    setSelectedObjective(objective);
    // Deep-link into review mode: ?reviewGrade=N (only the grade directly below the student's).
    const rg = Number(params.get('reviewGrade'));
    if (Number.isInteger(rg) && rg > 0 && !!user?.grade && rg === user.grade - 1) {
      setReviewGrade(prev => {
        if (prev !== rg) trackReviewEntered(params.get('src') || 'link', rg);
        return rg;
      });
    }
  }, [search, user?.grade]);


  useEffect(() => {
    if ((selectedTopic || selectedObjective) && selectedMode === 'quiz') {
      // Reset quiz-specific state when entering quiz mode
      setCurrentQuestionIndex(0);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setScore(0);
      setAnsweredQuestions(new Set());
      reviewRef.current = [];
      setCorrectQuestions(new Set());
      setTestStartTime(new Date());
      setOrderedItems([]);
      setSelectedCells(new Set());
      setMatchingSelections({});
      setActivePrompt(null);
      setShowAIExplanation(false);
      setAIExplanationText(null);
      setKidExplanation(null);
      setLastSubmissionCorrect(null);
      capturePostHogEvent('Quiz Started', {
        subject: selectedSubject,
        topic: selectedTopic,
      });
    }
  }, [selectedTopic, selectedMode]);

  // Initialize order-match items when question changes
  // Note: currentQuestion is read via closure (defined after this hook, but safe at callback time)
  useEffect(() => {
    if (currentQuestion?.questionType === 'order-match' && currentQuestion.options) {
      const items = currentQuestion.options.map((opt: any, idx: number) => ({
        id: opt.id || `order_${idx}`,
        text: typeof opt === 'string' ? opt : opt.text,
        originalIndex: idx
      }));
      // Shuffle items for the student
      setOrderedItems([...items].sort(() => Math.random() - 0.5));
      setTapSelectedItem(null);
    } else if (currentQuestion?.questionType !== 'order-match') {
      setOrderedItems([]);
      setTapSelectedItem(null);
    }

    // Reset table-grid selection when question changes
    if (currentQuestion?.questionType === 'table-grid') {
      setSelectedCells(new Set());
    }

    // Reset matching state when question changes
    if (currentQuestion?.questionType === 'matching') {
      setMatchingSelections({});
      setActivePrompt(null);
    }

    // Reset feedback and answer when question changes
    setShowFeedback(false);
    setSelectedAnswer(null);
    setLastSubmissionCorrect(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, reportedQuestionIds.size]);

  // Fetch real topic statistics from the database
  const { data: topicStatsData = [], isLoading: topicsLoading } = useQuery({
    queryKey: ['/api/topics/stats', selectedSubject, effectiveGrade],
    queryFn: async () => {
      const response = await fetch(`/api/topics/stats?grade=${effectiveGrade || 6}&subject=${selectedSubject}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!response.ok) throw new Error('Failed to fetch topic stats');
      return response.json();
    },
    enabled: !!isAuthenticated && !!selectedSubject,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch topics from API based on selected subject
  const selectedSubjectData = activeSubjects.find(s => s.name.toLowerCase() === selectedSubject);
  
  const { data: apiTopics = [] } = useQuery<Array<{id: string; subjectId: string; name: string; displayName: string; description?: string | null; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/curriculum/topics', selectedSubjectData?.id],
    queryFn: async () => {
      if (!selectedSubjectData?.id) return [];
      const response = await fetch(`/api/curriculum/topics?subjectId=${selectedSubjectData.id}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!response.ok) throw new Error('Failed to fetch topics');
      return response.json();
    },
    enabled: !!selectedSubjectData?.id,
  });

  // Fetch reading materials for the selected topic
  const { data: topicReadingMaterials = EMPTY_READING, isLoading: readingMaterialsLoading, isSuccess: readingMaterialsReady } = useQuery<Array<{
    id: string;
    title: string;
    slug: string;
    description: string | null;
    estimatedReadTime: number | null;
    status: string;
    progress?: { status: string; scrollPosition: number; totalTimeSeconds: number };
  }>>({
    queryKey: ['/api/reading', selectedTopic, selectedSubject],
    queryFn: async () => {
      const response = await fetch(`/api/reading?topic=${encodeURIComponent(selectedTopic)}&subject=${selectedSubject}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!response.ok) throw new Error('Failed to fetch reading materials');
      return response.json();
    },
    enabled: !!selectedTopic && !!isAuthenticated,
  });

  // Interactive lessons for the selected topic
  const { data: topicLessons = EMPTY_LESSONS, isLoading: lessonsLoading, isSuccess: lessonsReady } = useQuery<Array<{
    id: string;
    title: string;
    slug: string;
    subject: string;
    topic: string;
    description: string | null;
    emoji: string | null;
    estimatedMinutes: number;
    xpReward: number;
    checkCount: number;
    progress: { status: "not_started" | "in_progress" | "completed"; score: number; totalChecks: number; xpAwarded: number } | null;
  }>>({
    queryKey: ['/api/lessons', selectedTopic, selectedSubject],
    queryFn: async () => {
      const response = await fetch(`/api/lessons?topic=${encodeURIComponent(selectedTopic!)}&subject=${encodeURIComponent(selectedSubject!)}`, { credentials: 'include', headers: getExtraHeaders() });
      if (!response.ok) throw new Error('Failed to fetch lessons');
      return response.json();
    },
    enabled: !!selectedTopic && !!selectedSubject && !!isAuthenticated && !reviewGrade,
  });

  // Published lesson counts per topic (badge on topic cards)
  const { data: lessonTopicCounts = EMPTY_LESSONS } = useQuery<Array<{ subject: string; topic: string; count: number }>>({
    queryKey: ['/api/lessons/topic-counts'],
    enabled: !!isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
  const lessonCountFor = (topicId: string): number => {
    if (!selectedSubject) return 0;
    const subj = subjectKey(selectedSubject);
    return lessonTopicCounts
      .filter(c => subjectKey(c.subject) === subj && c.topic.toLowerCase() === topicId)
      .reduce((sum, c) => sum + c.count, 0);
  };

  const activeTopics = apiTopics
    .filter(t => t.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Create topics with dynamic metadata, merging with real stats data
  const topics: Topic[] = activeTopics.map((topic, index) => {
    const stat = topicStatsData.find((s: any) => s.id === topic.name.toLowerCase());
    const metadata = getTopicMetadata(topic.displayName, selectedSubjectData?.displayName || '', index, topic.description);
    
    return {
      id: topic.name.toLowerCase(),
      name: topic.displayName,
      ...metadata,
      questionsCount: stat?.questionsCount || 0,
      completed: stat?.completed || 0,
      progress: stat?.progress || 0
    };
  });


  const currentTopic = topics.find(t => t.id === selectedTopic);
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>([]);
  
  useEffect(() => {
    if (fetchedQuestions && fetchedQuestions.length > 0 && selectedMode === 'quiz' && (selectedTopic || selectedObjective)) {
      setShuffledQuestions(shuffleWithGroups(fetchedQuestions));
    } else {
      setShuffledQuestions(fetchedQuestions || []);
    }
  }, [fetchedQuestions, selectedMode]);
  
  const topicQuestions = shuffledQuestions.filter(q => !reportedQuestionIds.has(q.id));
  const safeIndex = Math.min(currentQuestionIndex, Math.max(0, topicQuestions.length - 1));
  const currentQuestion = topicQuestions[safeIndex];
  currentQuestionIdRef.current = currentQuestion?.id;

  // Reset question start time when question changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestionIndex, currentQuestion?.id]);

  // Auto-skip mode selection: go straight to quiz when there's no reading or lesson content.
  // Only once BOTH queries have actually succeeded: a disabled, errored or not-yet-started
  // query also reports "not loading" with empty data, which used to send students straight
  // into a quiz before the reading list had been fetched.
  // Use replace:true so the browser back button skips this intermediate URL
  useEffect(() => {
    if (
      selectedTopic &&
      !selectedMode &&
      readingMaterialsReady &&
      lessonsReady &&
      !readingMaterialsLoading &&
      !lessonsLoading &&
      topicReadingMaterials.length === 0 &&
      topicLessons.length === 0
    ) {
      setSelectedModeState('quiz');
      setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=quiz`, { replace: true });
    }
  }, [selectedTopic, selectedMode, readingMaterialsReady, lessonsReady, readingMaterialsLoading, topicReadingMaterials, lessonsLoading, topicLessons]);


  const handleAnswerSelect = (optionId: string) => {
    if (showFeedback) return;
    setSelectedAnswer(optionId);
  };

  const handleSubmitAnswer = async (override?: string) => {
    if (!currentQuestion) return;
    if (showFeedback) return;
    // An option click passes its id straight in: state has not updated yet at that point.
    const answerNow = override ?? selectedAnswer;
    if (override !== undefined) setSelectedAnswer(override);
    
    // Get the user's answer based on question type
    let userAnswer: string = '';
    
    if (currentQuestion.questionType === 'multi-select') {
      // For multi-select, convert array to sorted JSON string for consistent comparison
      if (Array.isArray(answerNow)) {
        userAnswer = JSON.stringify([...answerNow].sort());
      } else if (answerNow) {
        userAnswer = JSON.stringify([answerNow]);
      }
    } else if (currentQuestion.questionType === 'order-match') {
      // For order-match, get the current order of items as comma-separated IDs
      userAnswer = orderedItems.map(item => item.id).join(',');
    } else if (currentQuestion.questionType === 'table-grid') {
      // For table-grid, get selected cells as comma-separated indices
      userAnswer = Array.from(selectedCells).sort().join(',');
    } else if (currentQuestion.questionType === 'matching') {
      // For matching, JSON stringify the selections
      userAnswer = JSON.stringify(matchingSelections);
    } else {
      // For other types, use selected answer directly
      userAnswer = (typeof answerNow === 'string' ? answerNow : null) || '';
    }
    
    // Must have an answer to submit
    if (!userAnswer) return;
    
    setShowFeedback(true);

    // For multiple-choice questions, resolve the correct option ID from legacy formats.
    // This lookup can't live in the shared grader because it needs access to the question's
    // options array and the historical "correctAnswer was the option text" format.
    let resolvedCorrectAnswer = currentQuestion.correctAnswer;
    if (currentQuestion.questionType === 'multiple-choice') {
      const opts = currentQuestion.options ?? [];
      // If correctAnswer is already an option ID (for new questions), keep as-is.
      const alreadyId = typeof opts[0] === 'object' && opts.some((opt: any) => opt.id === currentQuestion.correctAnswer);
      if (!alreadyId) {
        // Legacy: find which option matches the correctAnswer text
        const correctIndex = opts.findIndex((opt: any) => {
          const optText = typeof opt === 'string' ? opt : opt.text;
          return optText === currentQuestion.correctAnswer;
        });
        if (correctIndex >= 0) {
          resolvedCorrectAnswer = typeof opts[correctIndex] === 'string'
            ? `legacy_${correctIndex}`
            : ((opts[correctIndex] as any).id || `fallback_${correctIndex}`);
        }
      }
    }

    // Pick the user-input shape that best matches the question type, then delegate
    // all comparison logic to the shared grader. This is the SAME function the server
    // uses so client-side feedback and server-recorded isCorrect can never diverge.
    let userInput: unknown;
    if (currentQuestion.questionType === 'multi-select') {
      userInput = Array.isArray(selectedAnswer) ? selectedAnswer : (selectedAnswer ? [selectedAnswer] : []);
    } else if (currentQuestion.questionType === 'order-match') {
      userInput = orderedItems.map((item: any) => item.id);
    } else if (currentQuestion.questionType === 'table-grid') {
      userInput = Array.from(selectedCells);
    } else if (currentQuestion.questionType === 'matching') {
      userInput = matchingSelections;
    } else {
      // fill-blank, multiple-choice, true-false, short-answer
      userInput = userAnswer;
    }

    const isCorrect = gradeAnswer(
      {
        questionType: currentQuestion.questionType,
        correctAnswer: resolvedCorrectAnswer,
        options: currentQuestion.options,
        strict: (currentQuestion as any).strict === true,
      },
      userInput
    );
    setLastSubmissionCorrect(isCorrect);
    {
      const optText = (v: unknown): string => {
        const raw = Array.isArray(v) ? v.join(', ') : String(v ?? '');
        const opts: any[] = Array.isArray(currentQuestion.options) ? currentQuestion.options : [];
        const hit = opts.find((o: any) => typeof o === 'object' && (o.id === raw)) ?? opts.find((o: any) => (typeof o === 'string' ? o : o?.text) === raw);
        return hit ? (typeof hit === 'string' ? hit : hit.text) : raw;
      };
      reviewRef.current = [...reviewRef.current.filter(r => r.id !== currentQuestion.id), {
        id: currentQuestion.id, questionText: currentQuestion.questionText, chosen: optText(userAnswer),
        correct: optText(currentQuestion.correctAnswer), isCorrect, explanation: currentQuestion.explanation,
      }];
    }
    if (!isCorrect) {
      // Explain the slip straight away, in a 9 to 12 year old's terms, starting from what they picked.
      setKidExplanation(null); setAIExplanationText(null); setShowAIExplanation(false);
      generateExplanationMutation.mutate({ questionId: currentQuestion.id, chosenAnswer: userAnswer });
    }
    const pointsEarned = isCorrect ? currentQuestion.points : 0;
    
    // Calculate time spent on this question (in seconds)
    const timeSpentMs = Date.now() - questionStartTime;
    const timeSpentSeconds = Math.round(timeSpentMs / 1000);
    
    if (isCorrect) {
      if (!reviewGrade) setScore(score + currentQuestion.points);
      setCorrectQuestions(prev => new Set(prev).add(currentQuestion.id));
    }
    setAnsweredQuestions(prev => new Set(prev).add(currentQuestion.id));

    // Save attempt to database if user is authenticated
    if (isAuthenticated && user) {
      try {
        const attemptSubject = (currentQuestion as any).subject || selectedSubject || 'mathematics';
        await recordAttemptMutation.mutateAsync({
          questionId: currentQuestion.id,
          subject: attemptSubject,
          topic: currentQuestion.topic,
          difficulty: currentQuestion.difficulty,
          selectedAnswer: userAnswer,
          correctAnswer: currentQuestion.correctAnswer,
          isCorrect,
          basePoints: currentQuestion.points,
          pointsEarned,
          timeSpent: timeSpentSeconds
        });

        const topicQuestions = fetchedQuestions || [];
        const answeredInTopic = Array.from(answeredQuestions).filter(id => 
          topicQuestions.some((q: any) => q.id === id)
        ).length + 1;
        
        const correctInTopic = Array.from(correctQuestions).filter(id => 
          topicQuestions.some((q: any) => q.id === id)
        ).length + (isCorrect ? 1 : 0);
        
        const totalPointsInTopic = topicQuestions
          .filter((q: any) => correctQuestions.has(q.id))
          .reduce((sum: number, q: any) => sum + q.points, 0) + pointsEarned;

        await updateProgressMutation.mutateAsync({
          subject: attemptSubject,
          topic: currentQuestion.topic,
          totalQuestions: answeredInTopic,
          correctAnswers: correctInTopic,
          totalPoints: totalPointsInTopic,
          ...(isLastQuestion ? { completedAt: new Date().toISOString() } : {})
        });
      } catch (error) {
        // Error is handled in mutation onError callbacks
        console.error('Failed to save progress:', error);
      }
    }
  };

  const handleNextQuestion = () => {
    if (safeIndex < topicQuestions.length - 1) {
      setCurrentQuestionIndex(safeIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setShowAIExplanation(false);
      setAIExplanationText(null);
      setKidExplanation(null);
    }
  };

  const isLastQuestion = safeIndex === topicQuestions.length - 1;

  const handlePreviousQuestion = () => {
    if (safeIndex > 0) {
      setCurrentQuestionIndex(safeIndex - 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setShowAIExplanation(false);
      setAIExplanationText(null);
      setKidExplanation(null);
    }
  };

  // Use the result recorded by handleSubmitAnswer (which calls the shared grader).
  // Previously this did `selectedAnswer === currentQuestion?.correctAnswer`, which always
  // returned false for multi-select / matching / order-match / table-grid because
  // selectedAnswer is an array or object for those types: causing the UI to show
  // "Not quite right" even when the answer was actually graded correct in the DB.
  const isCorrect = lastSubmissionCorrect === true;

  // Handle test completion with anti-grinding results
  const handleCompleteTest = async () => {
    if (!selectedTopic || !isAuthenticated || !user) return;
    
    // Reported questions are struck from the session, so they never count as missed.
    const topicQuestions = (fetchedQuestions || []).filter((q: any) => !reportedQuestionIds.has(q.id));
    const totalQuestions = topicQuestions.length;
    if (totalQuestions === 0) { setLocation(selectedObjective ? '/progress-report' : '/learning'); return; }
    
    // Count correct answers from the set we tracked during the quiz
    const correctAnswersCount = Array.from(correctQuestions).filter(id => !reportedQuestionIds.has(id)).length;
    
    const testEndTime = new Date();
    const totalTimeSpent = Math.floor((testEndTime.getTime() - testStartTime.getTime()) / 1000);
    
    // Determine the dominant difficulty based on questions answered
    const difficulties = topicQuestions.map((q: any) => q.difficulty);
    const difficultyCount = difficulties.reduce((acc: any, diff: string) => {
      acc[diff] = (acc[diff] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const dominantDifficulty = Object.entries(difficultyCount)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0][0] as string;
    
    try {
      const firstQ = topicQuestions[0] as any;
      const completionSubject = firstQ?.subject || selectedSubject || 'mathematics';
      const completionTopic = selectedTopic || firstQ?.topic || 'general';
      await testResultsMutation.mutateAsync({
        subject: completionSubject,
        topic: completionTopic,
        totalQuestions,
        correctAnswers: correctAnswersCount,
        totalTimeSpent,
        difficulty: dominantDifficulty
      });
    } catch (error) {
      console.error('Failed to complete test:', error);
      setLocation(selectedObjective ? '/progress-report' : '/learning');
    }
  };

  // Learning Mode Selection View
  // Quiz keyboard shortcuts: A-D or 1-4 pick (and check) an option, Enter checks or moves on.
  useEffect(() => {
    if (selectedMode !== 'quiz' || !currentQuestion) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // A popup (grade check, report dialog, select menu) owns the keyboard while it is open.
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="listbox"]')) return;
      if (t && t.closest('[role="dialog"], [role="combobox"], [role="menu"]')) return;
      const optionCount = currentQuestion.options?.length ?? 0;
      const optionIdAt = (i: number) => { const o: any = currentQuestion.options![i]; return typeof o === 'string' ? `legacy_${i}` : (o.id || `fallback_${i}`); };
      const listType = currentQuestion.questionType === 'multiple-choice' || currentQuestion.questionType === 'multi-select';
      if (e.key === 'Enter') {
        e.preventDefault();
        if (showFeedback) { isLastQuestion ? handleCompleteTest() : handleNextQuestion(); }
        else if (currentQuestion.questionType === 'multiple-choice' && focusedOption >= 0 && focusedOption < optionCount) { handleSubmitAnswer(optionIdAt(focusedOption)); }
        else if (selectedAnswer) { handleSubmitAnswer(); }
        return;
      }
      if (!showFeedback && listType && optionCount > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
        setFocusedOption(prev => prev < 0 ? (dir > 0 ? 0 : optionCount - 1) : (prev + dir + optionCount) % optionCount);
        return;
      }
      if (!showFeedback && currentQuestion.questionType === 'multi-select' && e.key === ' ' && focusedOption >= 0 && focusedOption < optionCount) {
        e.preventDefault();
        const id = optionIdAt(focusedOption);
        const cur = Array.isArray(selectedAnswer) ? selectedAnswer : [];
        setSelectedAnswer(cur.includes(id) ? (cur.length > 1 ? cur.filter((x: string) => x !== id) : null) : [...cur, id]);
        return;
      }
      if (showFeedback || currentQuestion.questionType !== 'multiple-choice' || !currentQuestion.options) return;
      const k = e.key.toLowerCase();
      const idx = /^[a-d]$/.test(k) ? k.charCodeAt(0) - 97 : /^[1-4]$/.test(k) ? Number(k) - 1 : -1;
      if (idx < 0 || idx >= currentQuestion.options.length) return;
      const opt: any = currentQuestion.options[idx];
      const optionId = typeof opt === 'string' ? `legacy_${idx}` : (opt.id || `fallback_${idx}`);
      e.preventDefault();
      handleSubmitAnswer(optionId);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => {
    if (!showFeedback) return;
    const el = document.querySelector('[data-testid="card-feedback-desktop"]');
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [showFeedback]);
  useEffect(() => {
    if (selectedMode === 'quiz') window.scrollTo({ top: 0 });
    setFocusedOption(-1);
  }, [safeIndex, selectedMode]);

  if (selectedTopic && !selectedMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-chart-2 text-white p-4 md:p-6">
          <div className="container max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}`)}
                data-testid="button-back-topics"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <currentTopic.icon className="w-8 h-8" />
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold" data-testid="text-topic-title">
                      {currentTopic.name}
                    </h1>
                    <p className="text-white/90 text-sm">
                      Choose how you want to learn this topic
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Quiz Mode */}
            <Card 
              className="hover-elevate transition-transform hover:scale-105 bg-gradient-to-br from-blue-500 to-blue-600 text-white"
              data-testid="card-mode-quiz"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PenTool className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Take a Test</h3>
                <p className="text-white/90 text-sm mb-4">
                  Practice with interactive questions and get instant feedback
                </p>
                <Badge className="bg-white/20 text-white hover:bg-white/30">
                  {topicQuestions.length || 0} questions available
                </Badge>
                <Button 
                  className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => {
                    setSelectedModeState('quiz');
                    setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=quiz`);
                  }}
                  data-testid="button-start-quiz"
                >
                  Start Quiz
                </Button>
              </CardContent>
            </Card>

            {/* Reading Mode */}
            <Card 
              className="hover-elevate cursor-pointer transition-transform hover:scale-105 bg-gradient-to-br from-purple-500 to-purple-600 text-white"
              onClick={() => {
                setSelectedModeState('reading');
                setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=reading`);
              }}
              data-testid="card-mode-reading"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Read Content</h3>
                <p className="text-white/90 text-sm mb-4">
                  Study detailed explanations and learning materials
                </p>
                {readingMaterialsLoading ? (
                  <Badge className="bg-white/20 text-white hover:bg-white/30">
                    Loading...
                  </Badge>
                ) : topicReadingMaterials.length > 0 ? (
                  <Badge className="bg-green-500/80 text-white hover:bg-green-500/90">
                    {topicReadingMaterials.length} Available
                  </Badge>
                ) : (
                  <Badge className="bg-white/20 text-white hover:bg-white/30">
                    Coming Soon
                  </Badge>
                )}
                <Button 
                  className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedModeState('reading');
                    setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=reading`);
                  }}
                  data-testid="button-start-reading"
                >
                  Start Reading
                </Button>
              </CardContent>
            </Card>
            {/* Interactive Lesson Mode */}
            <Card
              className="hover-elevate cursor-pointer transition-transform hover:scale-105 bg-gradient-to-br from-amber-500 to-orange-600 text-white"
              onClick={() => {
                setSelectedModeState('lesson');
                setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=lesson`);
              }}
              data-testid="card-mode-lesson"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Interactive Lesson</h3>
                <p className="text-white/90 text-sm mb-4">
                  Learn by doing: sort, match, tap and solve with Owlbert
                </p>
                {lessonsLoading ? (
                  <Badge className="bg-white/20 text-white hover:bg-white/30">Loading...</Badge>
                ) : topicLessons.length > 0 ? (
                  <Badge className="bg-emerald-500/80 text-white hover:bg-emerald-500/90">
                    {topicLessons.length} {topicLessons.length === 1 ? 'lesson' : 'lessons'} · {topicLessons.filter(l => l.progress?.status === 'completed').length} done
                  </Badge>
                ) : (
                  <Badge className="bg-white/20 text-white hover:bg-white/30">Coming Soon</Badge>
                )}
                <Button
                  className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedModeState('lesson');
                    setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=lesson`);
                  }}
                  data-testid="button-start-lesson"
                >
                  Start Lesson
                </Button>
              </CardContent>
            </Card>

            {/* Video Mode */}
            <Card 
              className="hover-elevate cursor-pointer transition-transform hover:scale-105 bg-gradient-to-br from-green-500 to-green-600 text-white"
              onClick={() => {
                setSelectedModeState('video');
                setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=video`);
              }}
              data-testid="card-mode-video"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Watch Videos</h3>
                <p className="text-white/90 text-sm mb-4">
                  Learn through engaging video lessons and tutorials
                </p>
                <Badge className="bg-white/20 text-white hover:bg-white/30">
                  Coming Soon
                </Badge>
                <Button 
                  className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedModeState('video');
                    setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=video`);
                  }}
                  data-testid="button-start-video"
                >
                  Watch Videos
                </Button>
              </CardContent>
            </Card>

          </div>

          {topicPrereq && (
            <button
              onClick={() => setLocation(`/learning?subject=${encodeURIComponent(topicPrereq.subject)}&topic=${encodeURIComponent(topicPrereq.topicDisplay)}&reviewGrade=${topicPrereq.grade}&mode=quiz&src=prereq`)}
              className="mt-6 w-full flex items-center gap-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-left hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
              data-testid="link-revise-prereq"
            >
              <BookOpen className="w-5 h-5 text-amber-600 shrink-0" />
              <span className="text-sm text-amber-900 dark:text-amber-200">New to this? Revise <span className="font-semibold">{topicPrereq.topicDisplay}</span> from Grade {topicPrereq.grade} first. No points, just practice.</span>
            </button>
          )}

          {/* Topic Overview */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                About This Topic
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {currentTopic?.description}
              </p>
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  <span>{currentTopic?.questionsCount} practice questions</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>{currentTopic?.completed} completed</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>Grade {user?.grade} Level</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Subject Selection View
  if (!selectedSubject) {
    const byId = new Map((overview?.subjects ?? []).map(o => [o.id, o]));
    const cards = activeSubjects.map((subject, index) => {
      const metadata = getTopicMetadata(subject.displayName, '', index, subject.description);
      const id = subject.name.toLowerCase();
      const ov = byId.get(id);
      return { id, name: subject.displayName, icon: metadata.icon, description: ov?.description || metadata.description, ov, style: subjectStyleFor(id), tilt: STICKER_TILT[index % STICKER_TILT.length] };
    });
    const recent = cards
      .filter(c => c.ov?.lastTopic)
      .sort((a, b) => new Date(b.ov!.lastTopic!.at).getTime() - new Date(a.ov!.lastTopic!.at).getTime())[0];
    const go = (id: string, topic?: string) => {
      setSelectedSubject(id);
      if (topic) { setSelectedTopicState(topic); setSelectedModeState(null); }
      setLocation(topic ? `/learning?subject=${id}&topic=${encodeURIComponent(topic)}` : `/learning?subject=${id}`);
    };
    const n = (v: number | undefined) => (v ?? 0).toLocaleString();

    return (
      <div className="min-h-screen bg-background">
        {/* Header: flat purple band with Owlbert */}
        <div className="bg-primary text-primary-foreground">
          <div className="container max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-7 flex items-center gap-3 md:gap-5">
            <Button variant="ghost" className="text-primary-foreground hover:bg-white/15 p-2 shrink-0" onClick={() => setLocation('/dashboard')} data-testid="button-back-dashboard" aria-label="Back to dashboard">
              <IcoBack className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="text-subjects-title">Grade {effectiveGrade} Subjects{reviewGrade ? ' (Review)' : ''}</h1>
              <p className="text-primary-foreground/80 text-sm md:text-base" data-testid="text-subjects-subtitle">Pick a subject. Each card shows what is inside and where you left off.</p>
              {canReview && (
                <div className="mt-3 inline-flex rounded-full bg-white/15 p-1" data-testid="toggle-review-grade">
                  <button
                    onClick={() => { setReviewGrade(null); setSelectedSubject(null); }}
                    className={`px-3 py-1 text-xs md:text-sm font-semibold rounded-full transition-colors ${!reviewGrade ? 'bg-white text-primary' : 'text-white/80'}`}
                    data-testid="button-my-grade"
                  >My grade</button>
                  <button
                    onClick={() => { const g = (user!.grade as number) - 1; setReviewGrade(g); setSelectedSubject(null); trackReviewEntered('toggle', g); }}
                    className={`px-3 py-1 text-xs md:text-sm font-semibold rounded-full transition-colors ${reviewGrade ? 'bg-white text-primary' : 'text-white/80'}`}
                    data-testid="button-review-grade"
                  >Review Grade {(user!.grade as number) - 1}</button>
                </div>
              )}
            </div>
            <div className="hidden sm:block shrink-0"><Owlbert name="owl-book" size={84} /></div>
          </div>
        </div>
        {reviewGrade && (
          <div className="bg-amber-100 text-amber-900 text-sm" data-testid="banner-review-mode">
            <div className="container max-w-6xl mx-auto px-4 md:px-6 py-2">
              Review mode: Grade {reviewGrade}. Practice here helps you revise and does not change your points, streak or leaderboard.
            </div>
          </div>
        )}

        <div className="container max-w-6xl mx-auto p-4 md:p-6 space-y-6">
          {recent && recent.ov?.lastTopic && (
            <div className={`rounded-xl border ${recent.style.card} p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid="card-continue-recent">
              <div className={`w-11 h-11 rounded-xl ${recent.style.sticker} flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><IcoHistory className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-muted-foreground">Pick up where you left off</div>
                <div className="text-base md:text-lg font-semibold truncate">{recent.ov.lastTopic.name} <span className="font-normal text-muted-foreground">in {recent.name}</span></div>
              </div>
              <Button className={`${recent.style.button} rounded-lg shrink-0`} onClick={() => go(recent.id, recent.ov!.lastTopic!.id)} data-testid="button-continue-recent">
                Continue <IcoChevron className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => {
              const IconComponent = c.icon;
              const st = c.style;
              const started = (c.ov?.answered ?? 0) > 0;
              const topicsTotal = c.ov?.topics ?? 0;
              const topicsStarted = Math.min(c.ov?.topicsStarted ?? 0, topicsTotal);
              const pct = topicsTotal ? Math.round((topicsStarted / topicsTotal) * 100) : 0;
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  className={`group rounded-xl border ${st.card} p-4 md:p-5 flex flex-col gap-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 ${st.ring}`}
                  onClick={() => go(c.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(c.id); } }}
                  data-testid={`card-subject-${c.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl ${st.sticker} flex items-center justify-center shrink-0 ${c.tilt} shadow-sm`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold leading-tight">{c.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {c.ov ? (
                          <>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`}><IcoLayers className="w-3 h-3" />{n(c.ov.topics)} topics</span>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`}><IcoList className="w-3 h-3" />{n(c.ov.questions)} questions</span>
                            {c.ov.lessons > 0 && <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`} data-testid={`chip-lessons-${c.id}`}><IcoSpark className="w-3 h-3" />{n(c.ov.lessons)} {c.ov.lessons === 1 ? 'lesson' : 'lessons'}</span>}
                            {c.ov.readers > 0 && <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`} data-testid={`chip-readers-${c.id}`}><IcoBook className="w-3 h-3" />{n(c.ov.readers)} {c.ov.readers === 1 ? 'reader' : 'readers'}</span>}
                          </>
                        ) : (
                          <span className={`inline-block h-5 w-28 rounded-full ${st.track} animate-pulse`} aria-hidden />
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2">{c.description}</p>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-muted-foreground">{started ? `${topicsStarted} of ${topicsTotal} topics started` : 'Not started yet'}</span>
                      <span className="flex items-center gap-2 tabular-nums">
                        {started && c.ov?.accuracy != null && <span className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full ${st.chip}`}><IcoTarget className="w-3 h-3" />{c.ov.accuracy}% right</span>}
                        {started && <span className="font-semibold">{pct}%</span>}
                      </span>
                    </div>
                    <div className={`h-2 rounded-full ${st.track} overflow-hidden`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                      <div className={`h-full rounded-full ${st.bar} transition-all duration-700`} style={{ width: `${Math.max(pct, started ? 3 : 0)}%` }} />
                    </div>
                    <div className={`text-xs ${st.text} truncate min-h-[1rem]`}>
                      {c.ov?.lastTopic ? <>Last: {c.ov.lastTopic.name}{c.ov.topicsStarted > 1 ? ` and ${c.ov.topicsStarted - 1} more ${c.ov.topicsStarted - 1 === 1 ? 'topic' : 'topics'}` : ''}</> : c.ov ? 'Owlbert is ready when you are.' : ''}
                    </div>
                  </div>

                  <Button className={`w-full ${st.button} rounded-lg mt-auto`} onClick={(e) => { e.stopPropagation(); go(c.id, started && c.ov?.lastTopic ? c.ov.lastTopic.id : undefined); }} data-testid={`button-select-${c.id}`}>
                    {started ? 'Continue' : 'Start learning'} <IcoChevron className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Topic Selection View
  if (!selectedTopic) {
    // Get the display name for the selected subject
    const selectedSubjectDisplayName = selectedSubjectData?.displayName || selectedSubject || '';
    const topicPageStyle = subjectStyleFor(selectedSubject);

    return (
      <div className="min-h-screen bg-background">
        {/* Header: flat purple band */}
        <div className="bg-primary text-primary-foreground">
          <div className="container max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-7 flex items-center gap-3 md:gap-5">
            <Button variant="ghost" className="text-primary-foreground hover:bg-white/15 p-2 shrink-0" onClick={() => setLocation('/learning')} data-testid="button-back-subjects" aria-label="Back to subjects">
              <IcoBack className="w-5 h-5" />
            </Button>
            <div className={`w-11 h-11 rounded-xl ${topicPageStyle.sticker} flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><Book className="w-6 h-6" /></div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="text-learning-title">Grade {user?.grade || 6} {selectedSubjectDisplayName}</h1>
              <p className="text-primary-foreground/80 text-sm md:text-base" data-testid="text-learning-subtitle">Pick a topic. Lessons, readers and quizzes live inside each one.</p>
            </div>
          </div>
        </div>

        <div className="container max-w-6xl mx-auto p-4 md:p-6">
          {topicsLoading ? (
            <div className="text-center py-12">
              <div className="text-lg text-muted-foreground">Loading topics...</div>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-lg text-muted-foreground">No topics available</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topics.map((topic, ti) => {
              const IconComponent = topic.icon;
              const st = topicPageStyle;
              const open = () => {
                setSelectedTopicState(topic.id);
                setSelectedModeState(null);
                setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(topic.id)}`);
              };
              return (
                <div
                  key={topic.id}
                  role="button"
                  tabIndex={0}
                  className={`group rounded-xl border ${st.card} p-4 md:p-5 flex flex-col gap-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 ${st.ring}`}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                  data-testid={`card-topic-${topic.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl ${st.sticker} flex items-center justify-center shrink-0 ${STICKER_TILT[ti % STICKER_TILT.length]} shadow-sm`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold leading-tight">{topic.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`}><IcoList className="w-3 h-3" />{topic.questionsCount.toLocaleString()} questions</span>
                        {lessonCountFor(topic.id) > 0 && (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`} data-testid={`badge-lessons-${topic.id}`}><IcoSpark className="w-3 h-3" />{lessonCountFor(topic.id)} {lessonCountFor(topic.id) === 1 ? 'lesson' : 'lessons'}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2">{topic.description}</p>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium text-muted-foreground">{topic.completed > 0 ? 'Progress' : 'Not started yet'}</span>
                      <span className="tabular-nums font-semibold">{topic.completed}/{topic.questionsCount}</span>
                    </div>
                    <div className={`h-2 rounded-full ${st.track} overflow-hidden`} role="progressbar" aria-valuenow={topic.progress} aria-valuemin={0} aria-valuemax={100}>
                      <div className={`h-full rounded-full ${st.bar} transition-all duration-700`} style={{ width: `${Math.max(topic.progress, topic.completed > 0 ? 3 : 0)}%` }} />
                    </div>
                  </div>

                  <Button className={`w-full ${st.button} rounded-lg mt-auto min-h-11`} onClick={(e) => { e.stopPropagation(); open(); }} data-testid={`button-start-${topic.id}`}>
                    {topic.completed > 0 ? 'Continue' : 'Start'} learning <IcoChevron className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Video Content View
  if (selectedTopic && selectedMode === 'video') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-modes"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <Play className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name} Videos
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          <Card className="hover-elevate">
            <CardContent className="p-8 text-center">
              <Play className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">Video Content Coming Soon!</h3>
              <p className="text-muted-foreground mb-6">
                We're working on creating engaging video lessons for {currentTopic?.name.toLowerCase()}. 
                Check back soon for interactive video content that will help you master this topic!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => {
                    setSelectedModeState('quiz');
                    setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=quiz`);
                  }}
                  data-testid="button-try-quiz"
                >
                  Try Quiz Instead
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                  data-testid="button-back-modes"
                >
                  Back to Learning Modes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reading Content View
  if (selectedTopic && selectedMode === 'lesson') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-modes"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name} Interactive Lessons
                  </h1>
                </div>
              )}
            </div>
            <p className="text-white/90 text-sm ml-12">Finish a lesson to earn XP. Get checks right on the first try for bonus XP.</p>
          </div>
        </div>

        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          {lessonsLoading ? (
            <Card className="hover-elevate">
              <CardContent className="p-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Loading Lessons...</h3>
              </CardContent>
            </Card>
          ) : topicLessons.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {topicLessons.map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} onOpen={() => setLocation(`/lessons/${lesson.slug}`)} />
              ))}
            </div>
          ) : (
            <Card className="hover-elevate">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Interactive Lessons Coming Soon!</h3>
                <p className="text-muted-foreground mb-6">
                  We're building hands-on lessons for {currentTopic?.name.toLowerCase()}. In the meantime, try a quiz.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => {
                      setSelectedModeState('quiz');
                      setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=quiz`);
                    }}
                    data-testid="button-try-quiz"
                  >
                    Try Quiz Instead
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                  >
                    Back to Learning Modes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  if (selectedTopic && selectedMode === 'reading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-modes"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name} Reading Materials
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          {readingMaterialsLoading ? (
            <Card className="hover-elevate">
              <CardContent className="p-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Loading Reading Materials...</h3>
                <p className="text-muted-foreground">
                  Finding reading content for {currentTopic?.name.toLowerCase()}
                </p>
              </CardContent>
            </Card>
          ) : topicReadingMaterials.length > 0 ? (
            <div className="space-y-4">
              {topicReadingMaterials.map((material) => (
                <Card 
                  key={material.id} 
                  className="hover-elevate cursor-pointer"
                  onClick={() => setLocation(`/reading/${material.slug}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-purple-500" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{material.title}</h3>
                          {material.progress?.status === 'completed' && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                          {material.progress?.status === 'in_progress' && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              <Clock className="w-3 h-3 mr-1" />
                              In Progress
                            </Badge>
                          )}
                        </div>
                        {material.description && (
                          <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{material.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {material.estimatedReadTime && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {material.estimatedReadTime} min read
                            </span>
                          )}
                          {material.progress?.totalTimeSeconds && material.progress.totalTimeSeconds > 0 && (
                            <span className="flex items-center gap-1">
                              Time spent: {Math.floor(material.progress.totalTimeSeconds / 60)} min
                            </span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="hover-elevate">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Reading Materials Coming Soon!</h3>
                <p className="text-muted-foreground mb-6">
                  We're preparing comprehensive reading materials and study guides for {currentTopic?.name.toLowerCase()}. 
                  These will include detailed explanations, examples, and practice problems to support your learning.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => {
                      setSelectedModeState('quiz');
                      setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}&mode=quiz`);
                    }}
                    data-testid="button-try-quiz"
                  >
                    Try Quiz Instead
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                    data-testid="button-back-modes"
                  >
                    Back to Learning Modes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Quiz Mode - Loading state
  if ((selectedTopic || selectedObjective) && selectedMode === 'quiz' && questionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-primary to-chart-2 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-topics"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <currentTopic.icon className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name}
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          <Card className="hover-elevate">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Loading Questions...</h3>
              <p className="text-muted-foreground">
                Preparing your {currentTopic?.name.toLowerCase()} questions
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Mode - Error state
  if ((selectedTopic || selectedObjective) && selectedMode === 'quiz' && questionsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-primary to-chart-2 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-topics"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <currentTopic.icon className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name}
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          <Card className="hover-elevate">
            <CardContent className="p-8 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Questions</h3>
              <p className="text-muted-foreground mb-4">
                We couldn't load questions for this topic. Please try again.
              </p>
              <Button 
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-modes-error"
              >
                Back to Learning Modes
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Mode - No questions available
  if ((selectedTopic || selectedObjective) && selectedMode === 'quiz' && !topicQuestions.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="bg-gradient-to-r from-primary to-chart-2 text-white p-4 md:p-6">
          <div className="container max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20 p-2"
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-topics"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic && (
                <div className="flex items-center gap-3">
                  <currentTopic.icon className="w-6 h-6" />
                  <h1 className="text-xl sm:text-2xl font-bold">
                    {currentTopic.name}
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="container max-w-4xl mx-auto p-4 md:p-6">
          <Card className="hover-elevate">
            <CardContent className="p-8 text-center">
              <Book className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Questions Available</h3>
              <p className="text-muted-foreground mb-4">
                Questions for this topic haven't been added yet. Please check back later or try another topic.
              </p>
              <Button 
                onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)}
                data-testid="button-back-modes-empty"
              >
                Back to Learning Modes
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Mode - Current question check
  if ((selectedTopic || selectedObjective) && selectedMode === 'quiz' && !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <Target className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No questions available</h2>
            <p className="text-muted-foreground mb-4">
              Questions for this topic are coming soon!
            </p>
            <Button onClick={() => setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`)} data-testid="button-back-modes">
              Back to Learning Modes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if ((selectedTopic || selectedObjective) && selectedMode === 'quiz') {
    const quizStyle = subjectStyleFor(selectedSubject || selectedSubjectData?.name);
    const answered = currentQuestion?.questionType === 'order-match' ? orderedItems.length > 0 :
      currentQuestion?.questionType === 'table-grid' ? selectedCells.size > 0 :
      currentQuestion?.questionType === 'matching' ? Object.keys(matchingSelections).length > 0 :
      !!selectedAnswer && (typeof selectedAnswer !== 'string' || selectedAnswer.trim() !== '');
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {/* Header: flat purple band */}
        <div className="shrink-0 bg-primary text-primary-foreground">
          <div className="container max-w-3xl mx-auto px-3 sm:px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center gap-2 md:gap-3">
              <Button variant="ghost" className="text-primary-foreground hover:bg-white/15 p-2 shrink-0" onClick={() => {
                    if (selectedObjective) {
                      setLocation('/progress-report');
                    } else if (topicReadingMaterials.length === 0) {
                      setLocation(`/learning?subject=${selectedSubject}`);
                    } else {
                      setLocation(`/learning?subject=${selectedSubject}&topic=${encodeURIComponent(selectedTopic)}`);
                    }
                  }}
                data-testid="button-back-modes" aria-label="Back">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {currentTopic ? (
                <>
                  <div className={`hidden sm:flex w-10 h-10 rounded-xl ${quizStyle.sticker} items-center justify-center shrink-0 -rotate-2 shadow-sm`}><currentTopic.icon className="w-5 h-5" /></div>
                  <h1 className="text-base sm:text-lg md:text-xl font-bold tracking-tight truncate" data-testid="text-topic-name">{currentTopic.name}</h1>
                </>
              ) : selectedObjective ? (
                <h1 className="text-base sm:text-lg md:text-xl font-bold tracking-tight truncate" data-testid="text-topic-name">Objective Practice</h1>
              ) : null}
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <span className="text-xs sm:text-sm font-semibold px-2.5 py-1 rounded-full bg-white/20 tabular-nums" data-testid="text-question-progress">{safeIndex + 1} of {topicQuestions.length}</span>
                {reviewGrade ? (
                  <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold px-2.5 py-1 rounded-full bg-amber-400 text-amber-950" data-testid="badge-review-active"><BookOpen className="w-3.5 h-3.5" />Review</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold px-2.5 py-1 rounded-full bg-amber-400 text-amber-950 tabular-nums" data-testid="text-current-score"><Zap className="w-3.5 h-3.5" />{score}</span>
                )}
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/25 overflow-hidden" role="progressbar" aria-valuenow={safeIndex + 1} aria-valuemin={0} aria-valuemax={topicQuestions.length} data-testid="progress-questions">
              <div className="h-full rounded-full bg-amber-400 transition-all duration-500" style={{ width: `${((safeIndex + 1) / Math.max(1, topicQuestions.length)) * 100}%` }} />
            </div>
          </div>
        </div>

      <div className="flex-1">
      <div className="container max-w-3xl mx-auto p-3 sm:p-4 md:p-6 pb-6">
        {currentQuestion.groupId && (() => {
          const stimulus = (fetchedQuestions || []).find(
            (q: any) => q.groupId === currentQuestion.groupId && q.groupStimulus
          )?.groupStimulus;
          const groupQuestions = (fetchedQuestions || []).filter((q: any) => q.groupId === currentQuestion.groupId);
          const partIndex = groupQuestions.findIndex((q: any) => q.id === currentQuestion.id);
          const partLabel = String.fromCharCode(65 + partIndex);
          const totalParts = groupQuestions.length;
          
          return stimulus ? (
            <Card className="mb-4">
              <CardHeader className="p-3 md:p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Badge variant="secondary">Part {partLabel} of {totalParts}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3 md:p-4 pt-0">
                <div className="prose prose-sm max-w-none dark:prose-invert text-sm leading-relaxed">
                  {/<[^>]+>/.test(stimulus) ? (
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stimulus) }} />
                  ) : (
                    <p className="whitespace-pre-wrap">{stimulus}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : partIndex >= 0 && totalParts > 1 ? (
            <div className="mb-3">
              <Badge variant="secondary">Part {partLabel} of {totalParts}</Badge>
            </div>
          ) : null;
        })()}
        <Card className="rounded-2xl border-border shadow-sm" data-testid="card-question">
          <CardHeader className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Badge 
                  className={`rounded-full border-0 ${
                    currentQuestion.difficulty === 'easy' ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200' :
                    currentQuestion.difficulty === 'medium' ? 'bg-amber-500/15 text-amber-800 dark:text-amber-200' :
                    'bg-rose-500/15 text-rose-800 dark:text-rose-200'
                  }`}
                  data-testid="badge-difficulty"
                >
                  {currentQuestion.difficulty.charAt(0).toUpperCase() + currentQuestion.difficulty.slice(1)}
                </Badge>
                <Badge className="rounded-full border-0 bg-primary/10 text-primary hover:bg-primary/10" data-testid="badge-points">
                  <Zap className="w-3 h-3 mr-1" />{reviewGrade ? 'Review' : `${currentQuestion.points} XP`}
                </Badge>
                {currentQuestion.groupId && (() => {
                  const gq = (fetchedQuestions || []).filter((q: any) => q.groupId === currentQuestion.groupId);
                  const pi = gq.findIndex((q: any) => q.id === currentQuestion.id);
                  return pi >= 0 && gq.length > 1 ? (
                    <Badge variant="outline">Part {String.fromCharCode(65 + pi)}</Badge>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setReportDialogOpen(true)}
                  title="Report this question"
                  className="text-destructive hover:text-destructive"
                >
                  <Flag className="w-4 h-4" />
                </Button>
                {user?.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLocation(`/admin?editQuestion=${currentQuestion.id}`)}
                    data-testid="button-edit-question-admin"
                    className="gap-1 hidden sm:inline-flex"
                  >
                    <Edit className="w-3 h-3" />
                    <span className="text-xs">Edit</span>
                  </Button>
                )}
                <div className="hidden sm:flex items-center gap-1.5 text-muted-foreground text-xs">
                  <Clock className="w-3.5 h-3.5" />
                  <span>No timer</span>
                </div>
              </div>
            </div>
            
            <QuestionRenderer
              text={currentQuestion.questionText}
              imageUrl={currentQuestion.questionImageUrl}
              mathExpression={currentQuestion.questionMath}
              className="text-base sm:text-xl md:text-2xl leading-snug sm:leading-relaxed"
            />
          </CardHeader>
          
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
            {/* Multiple Choice Options */}
            {currentQuestion.questionType === "multiple-choice" && currentQuestion.options && (
              <div className="space-y-3">
                {currentQuestion.options.map((option: string | { id?: string; text: string; imageUrl?: string; math?: string }, index: number) => {
                  const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                  
                  // Handle both string and object options
                  const optionText = typeof option === 'string' ? option : option.text;
                  const optionImageUrl = typeof option === 'object' ? option.imageUrl : undefined;
                  const optionMath = typeof option === 'object' ? option.math : undefined;
                  // Generate option ID for legacy string options, or use existing ID for new format
                  const optionId = typeof option === 'string' ? `legacy_${index}` : (option.id || `fallback_${index}`);
                  
                  // Determine correct option ID - handle both legacy text-based and new ID-based questions
                  const reviewOpts = currentQuestion.options ?? [];
                  const correctOptionId = (() => {
                    // If correctAnswer is already an option ID (for new questions)
                    if (typeof reviewOpts[0] === 'object' && reviewOpts.some((opt: any) => opt.id === currentQuestion.correctAnswer)) {
                      return currentQuestion.correctAnswer;
                    }
                    // Legacy: find which option matches the correctAnswer text
                    const correctIndex = reviewOpts.findIndex((opt: any) => {
                      const optText = typeof opt === 'string' ? opt : opt.text;
                      return optText === currentQuestion.correctAnswer;
                    });
                    if (correctIndex >= 0) {
                      return typeof reviewOpts[correctIndex] === 'string' ? `legacy_${correctIndex}` : ((reviewOpts[correctIndex] as any).id || `fallback_${correctIndex}`);
                    }
                    return currentQuestion.correctAnswer; // fallback
                  })();
                  
                  const isSelected = selectedAnswer === optionId;
                  const isCorrectOption = optionId === correctOptionId;
                  
                  let buttonClass = "w-full p-3 sm:p-4 text-left border-2 rounded-xl transition-all min-h-14 shadow-sm h-auto whitespace-normal " + (!showFeedback && focusedOption === index ? "ring-2 ring-primary ring-offset-2 ring-offset-background " : "");
                  if (showFeedback) {
                    if (isCorrectOption) {
                      buttonClass += "bg-emerald-500/10 border-emerald-500 text-emerald-900 dark:text-emerald-100";
                    } else if (isSelected && !isCorrectOption) {
                      buttonClass += "bg-rose-500/10 border-rose-500 text-rose-900 dark:text-rose-100";
                    } else {
                      buttonClass += "bg-muted/30 border-border text-muted-foreground opacity-70";
                    }
                  } else {
                    buttonClass += isSelected 
                      ? "bg-primary/10 border-primary text-foreground" 
                      : "bg-card border-border hover:border-primary/60 hover:bg-primary/5 hover:-translate-y-0.5 hover:shadow-md";
                  }
                  
                  return (
                    <Button
                      key={index}
                      variant="ghost"
                      className={buttonClass}
                      onClick={() => { handleAnswerSelect(optionId); handleSubmitAnswer(optionId); }}
                      disabled={showFeedback}
                      data-testid={`option-${optionLetter.toLowerCase()}`}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm ${
                          showFeedback && isCorrectOption ? 'bg-emerald-500 text-white' :
                          showFeedback && isSelected && !isCorrectOption ? 'bg-rose-500 text-white' :
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                        }`}>
                          {showFeedback && isCorrectOption ? (
                            <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : showFeedback && isSelected && !isCorrectOption ? (
                            <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : (
                            <span className="font-bold text-sm">{optionLetter}</span>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Option content */}
                          <div className="space-y-2">
                            {/* Option text */}
                            {/<[^>]+>/.test(optionText) ? (
                              <div 
                                className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(optionText) }}
                              />
                            ) : (
                              <div className="text-base leading-relaxed whitespace-pre-wrap">
                                <LatexText text={optionText} />
                              </div>
                            )}
                            
                            {/* Mathematical expression */}
                            {optionMath && (
                              <div className="py-1">
                                <MathRenderer 
                                  math={optionMath} 
                                  displayMode={false}
                                  className="text-sm"
                                />
                              </div>
                            )}
                            
                            {/* Option image */}
                            {optionImageUrl && (
                              <div className="py-1">
                                <img
                                  src={optionImageUrl}
                                  alt={`Option ${optionLetter} illustration`}
                                  className="max-w-full max-h-32 object-contain rounded border border-border"
                                  loading="lazy"
                                  data-testid={`option-${optionLetter.toLowerCase()}-image`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Multi-Select Options */}
            {currentQuestion.questionType === "multi-select" && currentQuestion.options && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-2">Select all correct answers:</p>
                {currentQuestion.options.map((option: string | { id?: string; text: string; imageUrl?: string; math?: string }, index: number) => {
                  const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                  
                  // Handle both string and object options
                  const optionText = typeof option === 'string' ? option : option.text;
                  const optionImageUrl = typeof option === 'object' ? option.imageUrl : undefined;
                  const optionMath = typeof option === 'object' ? option.math : undefined;
                  // Generate option ID for legacy string options, or use existing ID for new format
                  const optionId = typeof option === 'string' ? `legacy_${index}` : (option.id || `fallback_${index}`);
                  
                  // Determine correct option IDs - handle JSON array or comma-separated
                  const correctOptionIds = (() => {
                    try {
                      const parsed = JSON.parse(currentQuestion.correctAnswer);
                      return Array.isArray(parsed) ? parsed : [parsed];
                    } catch {
                      return currentQuestion.correctAnswer.includes(',')
                        ? currentQuestion.correctAnswer.split(',').map((s: string) => s.trim())
                        : [currentQuestion.correctAnswer];
                    }
                  })();
                  
                  const selectedAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : (selectedAnswer ? [selectedAnswer] : []);
                  const isSelected = selectedAnswers.includes(optionId);
                  const isCorrectOption = correctOptionIds.includes(optionId);
                  
                  let buttonClass = "w-full p-3 sm:p-4 text-left border-2 rounded-xl transition-all min-h-14 shadow-sm h-auto whitespace-normal " + (!showFeedback && focusedOption === index ? "ring-2 ring-primary ring-offset-2 ring-offset-background " : "");
                  if (showFeedback) {
                    if (isCorrectOption) {
                      buttonClass += "bg-emerald-500/10 border-emerald-500 text-emerald-900 dark:text-emerald-100";
                    } else if (isSelected && !isCorrectOption) {
                      buttonClass += "bg-rose-500/10 border-rose-500 text-rose-900 dark:text-rose-100";
                    } else {
                      buttonClass += "bg-muted/30 border-border text-muted-foreground opacity-70";
                    }
                  } else {
                    buttonClass += isSelected 
                      ? "bg-primary/10 border-primary text-foreground" 
                      : "bg-card border-border hover:border-primary/60 hover:bg-primary/5 hover:-translate-y-0.5 hover:shadow-md";
                  }
                  
                  const handleMultiSelect = () => {
                    if (showFeedback) return;
                    
                    const currentSelections = Array.isArray(selectedAnswer) ? selectedAnswer : [];
                    if (currentSelections.includes(optionId)) {
                      // Remove from selections
                      const newSelections = currentSelections.filter((id: string) => id !== optionId);
                      setSelectedAnswer(newSelections.length > 0 ? newSelections : null);
                    } else {
                      // Add to selections
                      setSelectedAnswer([...currentSelections, optionId]);
                    }
                  };
                  
                  return (
                    <Button
                      key={index}
                      variant="ghost"
                      className={buttonClass}
                      onClick={handleMultiSelect}
                      disabled={showFeedback}
                      data-testid={`option-${optionLetter.toLowerCase()}`}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <Checkbox 
                          checked={isSelected}
                          className="mt-1 pointer-events-none"
                          data-testid={`checkbox-${optionLetter.toLowerCase()}`}
                        />
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm ${
                          showFeedback && isCorrectOption ? 'bg-emerald-500 text-white' :
                          showFeedback && isSelected && !isCorrectOption ? 'bg-rose-500 text-white' :
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                        }`}>
                          {showFeedback && isCorrectOption ? (
                            <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : showFeedback && isSelected && !isCorrectOption ? (
                            <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          ) : (
                            <span className="font-bold text-sm">{optionLetter}</span>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Option content */}
                          <div className="space-y-2">
                            {/* Option text */}
                            {/<[^>]+>/.test(optionText) ? (
                              <div 
                                className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(optionText) }}
                              />
                            ) : (
                              <div className="text-base leading-relaxed whitespace-pre-wrap">
                                <LatexText text={optionText} />
                              </div>
                            )}
                            
                            {/* Mathematical expression */}
                            {optionMath && (
                              <div className="py-1">
                                <MathRenderer 
                                  math={optionMath} 
                                  displayMode={false}
                                  className="text-sm"
                                />
                              </div>
                            )}
                            
                            {/* Option image */}
                            {optionImageUrl && (
                              <div className="py-1">
                                <img
                                  src={optionImageUrl}
                                  alt={`Option ${optionLetter} illustration`}
                                  className="max-w-full max-h-32 object-contain rounded border border-border"
                                  loading="lazy"
                                  data-testid={`option-${optionLetter.toLowerCase()}-image`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Order Match Question Type */}
            {currentQuestion.questionType === "order-match" && orderedItems.length > 0 && (
              <div className="space-y-3 relative">
                <p className="text-sm text-muted-foreground mb-4">
                  <span className="hidden md:inline">Drag items to arrange them in the correct order.</span>
                  <span className="md:hidden">Hold and drag to reorder.</span>
                </p>
                <div ref={orderListRef}>
                {orderedItems.map((item, index) => {
                  const correctOrder = currentQuestion.options!.map((opt: any, idx: number) =>
                    opt.id || `order_${idx}`
                  ).join(',');
                  const userOrder = orderedItems.map(i => i.id).join(',');
                  const isCorrectOrder = showFeedback && (userOrder === correctOrder);
                  const isWrongOrder = showFeedback && (userOrder !== correctOrder);
                  const isBeingDragged = touchDraggingIdx === index;
                  const isDesktopDragged = draggedItem === index;
                  const itemText = typeof item.text === 'string' ? item.text.replace(/<[^>]+>/g, '') : item.text;

                  return (
                    <div
                      key={item.id}
                      draggable={!showFeedback}
                      onDragStart={(e) => {
                        setDraggedItem(index);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedItem === null || draggedItem === index) return;
                        const newItems = [...orderedItems];
                        const d = newItems[draggedItem];
                        newItems.splice(draggedItem, 1);
                        newItems.splice(index, 0, d);
                        setOrderedItems(newItems);
                        setDraggedItem(index);
                      }}
                      onDragEnd={() => {
                        setDraggedItem(null);
                        handleAnswerSelect(orderedItems.map(i => i.id).join(','));
                      }}
                      onTouchStart={(e) => {
                        if (showFeedback) return;
                        const touch = e.touches[0];
                        const el = e.currentTarget as HTMLElement;
                        const rect = el.getBoundingClientRect();
                        const ref = {
                          startY: touch.clientY, startX: touch.clientX, index, moved: false,
                          holdTimer: undefined as ReturnType<typeof setTimeout> | undefined,
                          itemHeight: rect.height
                        };
                        ref.holdTimer = setTimeout(() => {
                          ref.moved = true;
                          setTouchDraggingIdx(index);
                          setGhostPosition({ x: touch.clientX, y: touch.clientY, text: itemText });
                          scrollLockRef.current = true;
                          if (navigator.vibrate) navigator.vibrate(30);
                        }, 200);
                        touchDragRef.current = ref;
                      }}
                      onTouchMove={(e) => {
                        if (!touchDragRef.current || showFeedback) return;
                        const touch = e.touches[0];
                        // If hold timer hasn't fired yet (item not lifted),
                        // cancel drag and let page scroll normally
                        if (!touchDragRef.current.moved) {
                          const dy = Math.abs(touch.clientY - touchDragRef.current.startY);
                          if (dy > 10) {
                            if (touchDragRef.current.holdTimer) clearTimeout(touchDragRef.current.holdTimer);
                            touchDragRef.current = null;
                          }
                          return;
                        }
                        // Item is lifted: prevent scroll and handle drag
                        e.preventDefault();
                        setGhostPosition({ x: touch.clientX, y: touch.clientY, text: orderedItems[touchDragRef.current.index]?.text?.replace(/<[^>]+>/g, '') || '' });
                        const container = orderListRef.current;
                        if (!container) return;
                        const children = Array.from(container.children) as HTMLElement[];
                        const overIndex = children.findIndex(child => {
                          const rect = child.getBoundingClientRect();
                          return touch.clientY >= rect.top && touch.clientY <= rect.bottom;
                        });
                        if (overIndex >= 0 && overIndex !== touchDragRef.current.index) {
                          const newItems = [...orderedItems];
                          const d = newItems[touchDragRef.current.index];
                          newItems.splice(touchDragRef.current.index, 1);
                          newItems.splice(overIndex, 0, d);
                          setOrderedItems(newItems);
                          touchDragRef.current.index = overIndex;
                          setTouchDraggingIdx(overIndex);
                        }
                      }}
                      onTouchEnd={() => {
                        if (touchDragRef.current?.holdTimer) clearTimeout(touchDragRef.current.holdTimer);
                        if (touchDragRef.current?.moved) {
                          handleAnswerSelect(orderedItems.map(i => i.id).join(','));
                        }
                        touchDragRef.current = null;
                        setTouchDraggingIdx(null);
                        setGhostPosition(null);
                        scrollLockRef.current = false;
                      }}
                      onTouchCancel={() => {
                        if (touchDragRef.current?.holdTimer) clearTimeout(touchDragRef.current.holdTimer);
                        touchDragRef.current = null;
                        setTouchDraggingIdx(null);
                        setGhostPosition(null);
                        scrollLockRef.current = false;
                      }}
                      className={`flex items-center gap-3 p-3 mb-2 border-2 rounded-lg select-none ${
                        !showFeedback ? 'cursor-move' : ''
                      } ${
                        showFeedback && isCorrectOrder ? 'bg-green-50 dark:bg-green-900/30 border-green-500' :
                        showFeedback && isWrongOrder ? 'bg-red-50 dark:bg-red-900/30 border-red-500' :
                        isBeingDragged ? 'opacity-30 border-primary border-dashed' :
                        isDesktopDragged ? 'opacity-50 scale-95 border-primary' :
                        'bg-background border-muted hover:border-primary/50'
                      }`}
                      data-testid={`order-item-${index}`}
                    >
                      <div className="text-muted-foreground flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        showFeedback && isCorrectOrder ? 'bg-green-500 text-white' :
                        showFeedback && isWrongOrder ? 'bg-red-500 text-white' :
                        'bg-primary/10 dark:bg-primary/40 text-primary dark:text-primary-foreground'
                      }`}>
                        {index + 1}
                      </span>
                      {/<[^>]+>/.test(item.text) ? (
                        <div
                          className="flex-1 text-sm prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.text) }}
                        />
                      ) : (
                        <div className="flex-1 text-sm">{item.text}</div>
                      )}
                    </div>
                  );
                })}
                </div>
                {/* Floating ghost element that follows finger */}
                {ghostPosition && (
                  <div
                    className="fixed z-50 pointer-events-none px-4 py-3 bg-primary text-primary-foreground rounded-lg shadow-2xl text-sm font-medium max-w-[80vw] truncate border-2 border-primary"
                    style={{
                      left: ghostPosition.x - 100,
                      top: ghostPosition.y - 25,
                      transform: 'scale(1.05) rotate(1deg)',
                      opacity: 0.95,
                    }}
                  >
                    {ghostPosition.text}
                  </div>
                )}
              </div>
            )}

            {/* Table Grid Question Type */}
            {currentQuestion.questionType === "table-grid" && currentQuestion.options && currentQuestion.options[0] && typeof currentQuestion.options[0] === 'object' && 'rows' in currentQuestion.options[0] && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">Click on cells to select your answers:</p>
                {(() => {
                  const gridData: any = currentQuestion.options[0];
                  const rows = gridData.rows || 2;
                  const cols = gridData.cols || 2;
                  const cells = gridData.cells || [];
                  const rowHeaders = gridData.rowHeaders || [];
                  const colHeaders = gridData.colHeaders || [];
                  // Use explicit count if available, otherwise fallback to array length for legacy questions
                  const numRowHeaders = gridData.numRowHeaders ?? rowHeaders.length;
                  const numColHeaders = gridData.numColHeaders ?? colHeaders.length;
                  const correctCellsStr = currentQuestion.correctAnswer || '';
                  const correctCells = correctCellsStr ? correctCellsStr.split(',').map(Number) : [];
                  
                  // Check if answer is correct
                  const userCellsArray = Array.from(selectedCells).map(Number).sort((a: number, b: number) => a - b);
                  const correctCellsArray = correctCells.sort((a: number, b: number) => a - b);
                  const isCorrectGrid = showFeedback && 
                    userCellsArray.length === correctCellsArray.length &&
                    userCellsArray.every((val, idx) => val === correctCellsArray[idx]);
                  const isWrongGrid = showFeedback && !isCorrectGrid;

                  const hasRowHeaders = numRowHeaders > 0;
                  const hasColHeaders = numColHeaders > 0;

                  return (
                    <div 
                      className="grid gap-2" 
                      style={{
                        gridTemplateColumns: hasRowHeaders ? `auto repeat(${cols}, 1fr)` : `repeat(${cols}, 1fr)`
                      }}
                    >
                      {/* Top-left corner cell (empty when both headers exist) */}
                      {hasRowHeaders && hasColHeaders && (
                        <div className="min-h-12" />
                      )}
                      
                      {/* Column headers */}
                      {hasColHeaders && Array.from({ length: cols }).map((_, idx) => (
                        <div key={`col-header-${idx}`} className="min-h-12 flex items-center justify-center font-medium text-sm text-muted-foreground bg-muted/30 rounded-md p-2">
                          {/<[^>]+>/.test(colHeaders[idx]) ? (
                            <span 
                              className="prose prose-sm max-w-none dark:prose-invert text-center"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(colHeaders[idx]) }}
                            />
                          ) : (
                            <span>{colHeaders[idx] || `Col ${idx + 1}`}</span>
                          )}
                        </div>
                      ))}
                      
                      {/* Grid rows with optional row headers */}
                      {Array.from({ length: rows }).map((_, rowIdx) => (
                        <Fragment key={`row-${rowIdx}`}>
                          {/* Row header */}
                          {hasRowHeaders && rowIdx < numRowHeaders && (
                            <div className="min-h-16 flex items-center justify-center font-medium text-sm text-muted-foreground bg-muted/30 rounded-md p-2">
                              {/<[^>]+>/.test(rowHeaders[rowIdx]) ? (
                                <span 
                                  className="prose prose-sm max-w-none dark:prose-invert text-center"
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rowHeaders[rowIdx]) }}
                                />
                              ) : (
                                <span>{rowHeaders[rowIdx] || `Row ${rowIdx + 1}`}</span>
                              )}
                            </div>
                          )}
                          {/* Empty cell if no row header for this row */}
                          {hasRowHeaders && rowIdx >= numRowHeaders && (
                            <div className="min-h-16" />
                          )}
                          
                          {/* Grid cells for this row */}
                          {Array.from({ length: cols }).map((_, colIdx) => {
                            const idx = rowIdx * cols + colIdx;
                            const cellValue = cells[idx] || `Cell ${idx + 1}`;
                            const isSelected = selectedCells.has(idx.toString());
                            const isCorrectCell = correctCells.includes(idx);
                            
                            return (
                              <Button
                                key={idx}
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  if (showFeedback) return;
                                  
                                  const newSelected = new Set(selectedCells);
                                  if (isSelected) {
                                    newSelected.delete(idx.toString());
                                  } else {
                                    newSelected.add(idx.toString());
                                  }
                                  setSelectedCells(newSelected);
                                  handleAnswerSelect(Array.from(newSelected).join(','));
                                }}
                                disabled={showFeedback}
                                className={`min-h-16 p-3 text-sm ${
                                  showFeedback && isCorrectCell && isSelected ? 'bg-green-50 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-400' :
                                  showFeedback && isCorrectCell && !isSelected ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-500 text-orange-700 dark:text-orange-400' :
                                  showFeedback && !isCorrectCell && isSelected ? 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-400' :
                                  showFeedback && !isCorrectCell && !isSelected ? 'bg-muted/30 border-muted text-muted-foreground' :
                                  isSelected ? 'bg-primary/10 dark:bg-primary/30 border-primary text-primary dark:text-primary-foreground' : 'bg-background'
                                }`}
                                data-testid={`grid-cell-${idx}`}
                              >
                                <div className="flex flex-col items-center gap-1">
                                  {isSelected && !showFeedback && (
                                    <CheckCircle className="w-4 h-4" />
                                  )}
                                  {showFeedback && isCorrectCell && (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  )}
                                  {/<[^>]+>/.test(cellValue) ? (
                                    <span 
                                      className="prose prose-sm max-w-none dark:prose-invert"
                                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cellValue) }}
                                    />
                                  ) : (
                                    <span>{cellValue}</span>
                                  )}
                                </div>
                              </Button>
                            );
                          })}
                        </Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Fill-in-the-blank Question Type */}
            {currentQuestion.questionType === "fill-blank" && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Type your answer in the box below:</p>
                <input
                  type="text"
                  value={typeof selectedAnswer === 'string' ? selectedAnswer : ''}
                  onChange={(e) => !showFeedback && setSelectedAnswer(e.target.value)}
                  disabled={showFeedback}
                  placeholder="Your answer..."
                  data-testid="input-fill-blank"
                  className={`w-full px-4 py-3 rounded-lg border-2 text-base transition-all outline-none bg-background
                    ${showFeedback
                      ? isCorrect
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : 'border-muted focus:border-primary'
                    }`}
                />
                {showFeedback && !isCorrect && (
                  <p className="text-sm text-muted-foreground">
                    Correct answer: <span className="font-medium text-foreground">{currentQuestion.correctAnswer.split(',')[0]}</span>
                  </p>
                )}
              </div>
            )}

            {/* Matching Question Type */}
            {currentQuestion.questionType === "matching" && currentQuestion.options?.[0] && typeof currentQuestion.options[0] === 'object' && 'prompts' in currentQuestion.options[0] && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Tap a term, then tap its match.
                </p>
                {(() => {
                  const data = currentQuestion.options![0] as any;
                  const prompts = data.prompts || {};
                  const answers = data.answers || {};
                  const promptKeys = Object.keys(prompts);
                  const answerKeys = Object.keys(answers);
                  const usedAnswers = new Set(Object.values(matchingSelections));
                  const allMatched = Object.keys(matchingSelections).length === promptKeys.length;

                  let correctMatch: Record<string, string> = {};
                  try { correctMatch = JSON.parse(currentQuestion.correctAnswer); } catch {}

                  const assignAnswer = (pKey: string, aKey: string) => {
                    const newSelections = { ...matchingSelections, [pKey]: aKey };
                    setMatchingSelections(newSelections);
                    setActivePrompt(null);
                    if (Object.keys(newSelections).length === promptKeys.length) {
                      handleAnswerSelect(JSON.stringify(newSelections));
                    }
                  };

                  // Shuffle answer keys once (stored in state via shuffledAnswerKeys if available)
                  const displayAnswerKeys = answerKeys;

                  return (
                    <div className="space-y-4">
                      {/* Two-column layout: Terms on left, Answers on right */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* Left column: Terms/Prompts */}
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Terms</p>
                          {promptKeys.map((pKey) => {
                            const isActive = activePrompt === pKey;
                            const selectedAKey = matchingSelections[pKey];
                            const isCorrectPair = showFeedback && selectedAKey === correctMatch[pKey];
                            const isWrongPair = showFeedback && selectedAKey && selectedAKey !== correctMatch[pKey];
                            const matchColor = selectedAKey ? `${['#6366f1','#8b5cf6','#06b6d4','#ec4899','#3b82f6','#a855f7','#14b8a6','#f472b6'][promptKeys.indexOf(pKey) % 8]}` : undefined;

                            return (
                              <button
                                key={pKey}
                                onClick={() => {
                                  if (showFeedback) return;
                                  if (selectedAKey) {
                                    const next = { ...matchingSelections };
                                    delete next[pKey];
                                    setMatchingSelections(next);
                                    setSelectedAnswer(null);
                                  } else {
                                    setActivePrompt(isActive ? null : pKey);
                                  }
                                }}
                                className={`w-full p-3 border-2 rounded-lg text-sm text-left transition-all ${
                                  isCorrectPair ? 'border-green-500 bg-green-50 dark:bg-green-900/30' :
                                  isWrongPair ? 'border-red-500 bg-red-50 dark:bg-red-900/30' :
                                  isActive ? 'border-primary bg-primary/10 ring-2 ring-primary/30 scale-[1.02]' :
                                  selectedAKey ? 'bg-muted/50' :
                                  'border-border hover:border-primary/50'
                                }`}
                                style={selectedAKey && !showFeedback ? { borderColor: matchColor, borderLeftWidth: 4 } : undefined}
                              >
                                <span className="font-semibold">{prompts[pKey]}</span>
                                {selectedAKey && !showFeedback && (
                                  <span className="block text-xs text-muted-foreground mt-0.5">✓ matched</span>
                                )}
                                {showFeedback && isWrongPair && (
                                  <span className="block text-xs text-muted-foreground mt-0.5">✗ {answers[correctMatch[pKey]]}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Right column: Answers */}
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Answers</p>
                          {displayAnswerKeys.map((aKey) => {
                            const isUsed = usedAnswers.has(aKey);
                            const matchedPrompt = isUsed ? Object.entries(matchingSelections).find(([_, v]) => v === aKey)?.[0] : null;
                            const matchColor = matchedPrompt ? `${['#6366f1','#8b5cf6','#06b6d4','#ec4899','#3b82f6','#a855f7','#14b8a6','#f472b6'][promptKeys.indexOf(matchedPrompt) % 8]}` : undefined;
                            const isCorrect = showFeedback && matchedPrompt && matchingSelections[matchedPrompt] === correctMatch[matchedPrompt];

                            return (
                              <button
                                key={aKey}
                                onClick={() => {
                                  if (showFeedback) return;
                                  if (isUsed) {
                                    // Unassign this answer
                                    const pk = Object.entries(matchingSelections).find(([_, v]) => v === aKey)?.[0];
                                    if (pk) {
                                      const next = { ...matchingSelections };
                                      delete next[pk];
                                      setMatchingSelections(next);
                                      setSelectedAnswer(null);
                                    }
                                    return;
                                  }
                                  if (activePrompt) {
                                    assignAnswer(activePrompt, aKey);
                                  } else {
                                    const firstUnmatched = promptKeys.find(pk => !matchingSelections[pk]);
                                    if (firstUnmatched) {
                                      setActivePrompt(firstUnmatched);
                                      assignAnswer(firstUnmatched, aKey);
                                    }
                                  }
                                }}
                                disabled={showFeedback}
                                className={`w-full p-3 border-2 rounded-lg text-sm text-left transition-all ${
                                  showFeedback && isCorrect ? 'border-green-500 bg-green-50 dark:bg-green-900/30' :
                                  showFeedback && isUsed ? 'border-red-500 bg-red-50 dark:bg-red-900/30' :
                                  isUsed ? 'opacity-50 bg-muted/30' :
                                  activePrompt ? 'border-primary/50 hover:border-primary hover:bg-primary/5 hover:scale-[1.02]' :
                                  'border-border hover:border-primary/50'
                                }`}
                                style={isUsed && !showFeedback ? { borderColor: matchColor, borderLeftWidth: 4 } : undefined}
                              >
                                {answers[aKey]}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Reset */}
                      {!showFeedback && Object.keys(matchingSelections).length > 0 && (
                        <button
                          className="text-xs text-muted-foreground underline"
                          onClick={() => {
                            setMatchingSelections({});
                            setActivePrompt(null);
                            setSelectedAnswer(null);
                          }}
                        >
                          Reset all matches
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

          {/* Inline feedback (every size) */}
          <div>
            {showFeedback && (
              <div className={`mt-5 rounded-xl border p-4 ${isCorrect ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`} data-testid="card-feedback-desktop">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm -rotate-2 ${isCorrect ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                    {isCorrect ? <CheckCircle className="w-5 h-5 text-white" /> : <XCircle className="w-5 h-5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h3 className={`font-bold text-lg ${isCorrect ? 'text-emerald-800 dark:text-emerald-200' : 'text-rose-800 dark:text-rose-200'}`}>
                        {isCorrect ? 'Correct!' : 'Not quite right'}
                      </h3>
                      {isCorrect && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white flex items-center gap-1">
                          <Zap className="w-3 h-3" />+{currentQuestion.points} XP
                        </span>
                      )}
                    </div>
                    <div className="text-sm leading-relaxed text-foreground/90">
                      <ExplanationRenderer
                        text={currentQuestion.explanation}
                        imageUrl={currentQuestion.explanationImageUrl}
                        mathExpression={currentQuestion.explanationMath}
                        className="leading-relaxed"
                      />
                    </div>
                    {!isCorrect && (
                      <div className="mt-4 pt-3 border-t border-current/10">
                        {!showAIExplanation && !aiExplanationText && !currentQuestion.aiExplanation ? (
                          <Button
                            onClick={() => generateExplanationMutation.mutate({ questionId: currentQuestion.id, chosenAnswer: typeof selectedAnswer === "string" ? selectedAnswer : JSON.stringify(selectedAnswer) })}
                            disabled={generateExplanationMutation.isPending}
                            variant="outline"
                            size="sm"
                            data-testid="button-generate-ai-explanation-desktop"
                          >
                            {generateExplanationMutation.isPending ? (
                              <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary mr-2" />Working it out for you...</>
                            ) : (
                              <><Lightbulb className="w-3.5 h-3.5 mr-2" />Explain it to me</>
                            )}
                          </Button>
                        ) : (showAIExplanation || aiExplanationText || currentQuestion.aiExplanation) ? (
                          <div data-testid="ai-explanation-display-desktop">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Lightbulb className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs font-medium text-muted-foreground">AI Tutor:</span>
                            </div>
                            {kidExplanation ? <KidExplanationPanel kid={kidExplanation} /> : (
                              <div className="bg-muted/50 rounded-md px-3 py-2 text-sm leading-relaxed whitespace-pre-line">
                                {aiExplanationText || currentQuestion.aiExplanation}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          </CardContent>
        </Card>
      </div>
      </div>

      {/* Action bar: sticks to the bottom of the viewport on every screen size */}
      <div className="sticky bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm" data-testid="card-feedback">
        <div className="container max-w-3xl mx-auto px-3 sm:px-4 md:px-6 py-3 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" className="rounded-lg text-muted-foreground shrink-0" onClick={handlePreviousQuestion} disabled={safeIndex === 0} data-testid="button-previous-desktop">
            <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Previous</span>
          </Button>
          <div className="flex-1 flex justify-center">
            {!showFeedback ? (
              <Button onClick={() => handleSubmitAnswer()} disabled={!answered} className="h-12 px-8 text-base rounded-xl w-full sm:w-auto sm:min-w-[16rem]" data-testid="button-submit-desktop">
                {answered ? 'Check answer' : currentQuestion.questionType === 'multiple-choice' ? 'Tap an answer to check it' : 'Pick an answer first'}
                {answered && <CheckCircle className="w-4 h-4 ml-2" />}
              </Button>
            ) : isLastQuestion ? (
              <Button onClick={handleCompleteTest} disabled={testResultsMutation.isPending} className="h-12 px-8 text-base rounded-xl w-full sm:w-auto sm:min-w-[16rem]" data-testid="button-complete-topic-desktop">
                {testResultsMutation.isPending ? 'Saving...' : 'See my results'}<CheckCircle className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleNextQuestion} className="h-12 px-8 text-base rounded-xl w-full sm:w-auto sm:min-w-[16rem]" data-testid="button-next-desktop">
                Next question<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
          <span className="hidden md:inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0 w-36 justify-end" aria-hidden>
            {!showFeedback && <><kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono text-[10px]">↑↓</kbd>move </>}<kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono text-[10px]">Enter</kbd>{showFeedback ? 'next' : 'check'}
          </span>
        </div>
      </div>

      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Question</DialogTitle>
            <DialogDescription>
              Help us improve by letting us know what's wrong with this question.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <RadioGroup value={reportReason} onValueChange={setReportReason}>
              {[
                { value: "incorrect_answer", label: "Incorrect Answer" },
                { value: "unclear_question", label: "Unclear Question" },
                { value: "wrong_topic", label: "Wrong Topic" },
                { value: "offensive_content", label: "Offensive Content" },
                { value: "duplicate", label: "Duplicate Question" },
                { value: "other", label: "Other" },
              ].map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`reason-${opt.value}`} />
                  <Label htmlFor={`reason-${opt.value}`}>{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
            <div className="space-y-2">
              <Label>
                Additional Details{" "}
                {reportReason === "other" ? (
                  <span className="text-destructive">(required)</span>
                ) : (
                  <span className="text-muted-foreground">(optional)</span>
                )}
              </Label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder={reportReason === "other" ? "Please describe the issue…" : "Provide more details about the issue..."}
                className="resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!reportReason || (reportReason === "other" && !reportDetails.trim()) || reportQuestionMutation.isPending}
              onClick={() => {
                if (currentQuestion && reportReason) {
                  reportQuestionMutation.mutate({
                    questionId: currentQuestion.id,
                    reason: reportReason,
                    details: reportDetails || undefined,
                  });
                }
              }}
            >
              {reportQuestionMutation.isPending ? "Submitting..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    );
  }

  // Fallback - should not reach here
  return null;
};

export default Learning;